import { GridFilterItem } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import {
  CLIENT_CUSTOMER_DEFAULT_VISIBLE_FIELDS,
  DEFAULT_VISIBLE_FIELDS,
  buildClientOptions,
  buildContractOptions,
  buildColumnDefs,
  buildFilterModel,
  buildInitialColumnSelection,
  buildInitialFilterModel,
  buildStatusOptions,
  buildStatusGroups,
  buildJobTypeOptions,
  cleanSavedReportFilterModel,
  getOpenStatusFilterModel,
  getOpenStatusNames,
  hasServiceStatusPredicate,
} from './serviceListFilters';
import type { LabelValue } from './serviceListFilters';
import { parseServiceListUrl, writeServiceListUrl } from './serviceListUrlState';
import type { ServiceListDataReturn, SortState } from './useServiceListData.types';
import { useServiceLookups } from './useServiceLookups';
import { ServiceListDataType } from '../../@types/service.type';
import { NodeType } from '../../@types/structure.type';
import { ServiceQueryOptions } from '../../api/services';
import { FilterState, OptionGroup } from '../../components/molecules/FilterBar';
import {
  ColumnDef,
  reorderByVisibleIndex,
  applyColumnOrder,
} from '../../components/organisms/ServiceTable';
import { ColumnSelectionItem } from '../../components/templates/ColumnSelection';
import { StructureTypeCategory } from '../../configs/enums';
import {
  SERVICE_ADDRESS_SPLIT_FIELDS,
  SERVICE_ADDRESS_SPLIT_TOGGLE_FIELD,
  SERVICE_DEFAULT_REPORT_TYPES,
} from '../../constants/service.constant';
import { useColumnRefetchScheduler } from '../../hooks/useColumnRefetchScheduler';
import { useRefetchLoopGuard } from '../../hooks/useRefetchLoopGuard';
import { useSessionColumns } from '../../hooks/useSessionColumns';
import {
  useGetServicePaginatedData,
  useGetServiceActionProcessTypes,
} from '../../queries/service-query';
import { useGetServicesStatuses } from '../../queries/service-status-query';
import { useGetStructuresByTypeCategoryCodeAndTypeCode } from '../../queries/structure-query';
import type { SortToken } from '../../utils/listUrlState';
import {
  applySessionColumnState,
  dropColumnWidths,
  readSessionColumnState,
} from '../../utils/sessionColumnState';

// Re-export for consumers
export { filterModelToFilterState } from './serviceListFilters';
export type { LabelValue, ServiceListDataReturn };

const getVisibleFields = (data: ColumnSelectionItem[]): Set<string> => {
  const fields = new Set<string>();
  for (const group of data) {
    for (const col of group.value) {
      if (!col.value) continue;
      if (col.field === SERVICE_ADDRESS_SPLIT_TOGGLE_FIELD) {
        for (const f of SERVICE_ADDRESS_SPLIT_FIELDS) fields.add(f);
        continue;
      }
      fields.add(col.field);
    }
  }
  return fields;
};

const DEFAULT_SORT: SortToken = { field: 'serviceId', direction: 'desc' };

const SESSION_PAGE_NAME = 'ServiceList';

// Build the initial column defs, layering any browser-session layout
// (order/visibility/widths) on top of the defaults. A saved report still wins —
// it's applied afterwards via applyReportConfig.
const buildSessionColumns = (fields: ReadonlyArray<string>): ColumnDef[] =>
  applySessionColumnState(buildColumnDefs(fields), readSessionColumnState(SESSION_PAGE_NAME));

// The session stores visible column KEYS, but the picker's address-split master
// toggle maps to seven underlying split columns (and colSelectionData drives
// which columns the BFF fetches). Re-tick the toggle when any split column is
// visible so the picker and the fetched columns stay in sync with the columns.
const buildSessionColSelection = (fields: ReadonlyArray<string>): ColumnSelectionItem[] => {
  const cols = buildSessionColumns(fields);
  const visibleKeys = new Set(cols.filter((c) => c.visible).map((c) => c.key));
  const pickerFields = [...visibleKeys];
  if (SERVICE_ADDRESS_SPLIT_FIELDS.some((f) => visibleKeys.has(f))) {
    pickerFields.push(SERVICE_ADDRESS_SPLIT_TOGGLE_FIELD);
  }
  return buildInitialColumnSelection(pickerFields);
};

export const useServiceListData = (isClient = false): ServiceListDataReturn => {
  const defaultVisibleFields = isClient
    ? CLIENT_CUSTOMER_DEFAULT_VISIBLE_FIELDS
    : DEFAULT_VISIBLE_FIELDS;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const locationId = searchParams.get('locationId');

  // Activity monitor deep-links: /services?clientCode=X&serviceTypeName=Y&serviceStatusGroup=Z
  const clientCode = searchParams.get('clientCode');
  const serviceTypeName = searchParams.get('serviceTypeName');
  const serviceStatusGroup = searchParams.get('serviceStatusGroup');

  // Import batch deep-links: /services?importSessionId=X
  const importSessionId = searchParams.get('importSessionId');

  // Derive defaultReport from URL path (e.g. /services/myServices → "myServices")
  const defaultReport = useMemo(
    () => SERVICE_DEFAULT_REPORT_TYPES.find((r) => location.pathname.includes(r)),
    [location.pathname]
  );

  // Structure list "View Jobs" deep-links here with `?structureNodeId=X` so
  // we land with the list pre-scoped to that location's jobs.
  const structureNodeIdParam = useMemo(
    () => new URLSearchParams(location.search).get('structureNodeId'),
    [location.search]
  );

  // Hydrate one-shot from the URL on first render. Subsequent state lives
  // in `useState` and is mirrored back to the URL via the writer effect
  // below; the parser is only re-read on a fresh mount (e.g. after the
  // user hits Back from a service detail page).
  const initialUrlStateRef = useRef(parseServiceListUrl(searchParams));
  const initialUrlState = initialUrlStateRef.current;

  const [page, setPage] = useState(initialUrlState.page ?? 0);
  const [pageSize, setPageSize] = useState(initialUrlState.pageSize ?? 25);
  const [sortModel, setSortModel] = useState<SortState>(() => {
    if (initialUrlState.sort) {
      return { field: initialUrlState.sort.field, sort: initialUrlState.sort.direction };
    }
    return locationId ? { field: '', sort: null } : { field: 'serviceId', sort: 'desc' };
  });
  const [filterModel, setFilterModel] = useState<{ items: GridFilterItem[] }>(() => {
    if (initialUrlState.hasChipState) {
      // Layer importSessionId/structureNodeId scope predicates on top of
      // chip-derived items so the BFF still receives them when the user
      // landed via those deep-links and then refined with chips.
      const items = [...initialUrlState.filterItems];
      if (importSessionId && !items.some((i) => i.field === 'importSessionId')) {
        items.push({ field: 'importSessionId', operator: 'equals', value: importSessionId });
      }
      if (structureNodeIdParam && !items.some((i) => i.field === 'structureNodeId')) {
        items.push({ field: 'structureNodeId', operator: 'equals', value: structureNodeIdParam });
      }
      return { items };
    }
    return buildInitialFilterModel({
      locationId,
      importSessionId,
      clientCode,
      serviceTypeName,
      serviceStatusGroup,
      structureNodeId: structureNodeIdParam,
      defaultReport,
    });
  });
  // Reset sort/filter when locationId changes without a remount (same pathname).
  // Skip the initial render — the useState initialisers above already set the
  // correct state from URL params (deep-link filters, etc.).
  const locationIdInitialised = useRef(false);
  // True once a non-empty filter model has been applied for this view —
  // either from a deep-link, a saved report, the open-statuses default, or
  // explicit user input. Gates the open-statuses default effect below so
  // it only fires for fresh mounts that haven't received another source
  // of truth yet. Pre-armed when the URL already carries chip state so a
  // back-button remount doesn't re-apply the open-statuses default on top
  // of the user's previously-selected chips.
  const filtersInitialisedRef = useRef(initialUrlState.hasChipState);
  // Set when applyReportConfig stripped a legacy `not Complete` /
  // `not Cancelled` pair from a saved report and the statuses query
  // hadn't resolved yet — the open-statuses migration is deferred until
  // it does. The migration effect below clears this once it completes
  // (or once the user has overridden the status filter manually).
  const [pendingOpenStatusMigration, setPendingOpenStatusMigration] = useState(false);
  useEffect(() => {
    if (!locationIdInitialised.current) {
      locationIdInitialised.current = true;
      return;
    }
    if (locationId) {
      setSortModel({ field: '', sort: null });
      setFilterModel({ items: [] });
      filtersInitialisedRef.current = true;
    } else {
      setSortModel({ field: 'serviceId', sort: 'desc' });
      setFilterModel({ items: [] });
      // Re-arm the open-statuses default for the post-locationId view.
      filtersInitialisedRef.current = false;
    }
    setPage(0);
  }, [locationId]);

  const [multiSearchQuery, setMultiSearchQuery] = useState(initialUrlState.multiSearchQuery);
  const [columns, setColumns] = useState<ColumnDef[]>(() =>
    buildSessionColumns(defaultVisibleFields)
  );
  const [colSelectionData, setColSelectionData] = useState<ColumnSelectionItem[]>(() =>
    buildSessionColSelection(defaultVisibleFields)
  );
  const { resetColumnsToDefault, restoreDefaultColumns, setColumnWidth } = useSessionColumns({
    setColumns,
    setColSelectionData,
    buildColumns: () => buildSessionColumns(defaultVisibleFields),
    buildColSelection: () => buildSessionColSelection(defaultVisibleFields),
    sessionPageName: SESSION_PAGE_NAME,
  });
  // useIsClient is fail-closed (true while loading): the initial columns
  // above reflect that closed state. Once visibility resolves and the user
  // turns out NOT to be a client, swap to the standard column set so the
  // table doesn't stay stuck on the curated client view. The flip only ever
  // runs at most once per mount — from client (loading) → non-client.
  const lastIsClientRef = useRef(isClient);
  useEffect(() => {
    if (lastIsClientRef.current === isClient) return;
    lastIsClientRef.current = isClient;
    const nextFields = isClient ? CLIENT_CUSTOMER_DEFAULT_VISIBLE_FIELDS : DEFAULT_VISIBLE_FIELDS;
    setColumns(buildSessionColumns(nextFields));
    setColSelectionData(buildSessionColSelection(nextFields));
  }, [isClient]);
  const [fetchDurationMs, setFetchDurationMs] = useState<number | null>(null);
  const fetchStartRef = useRef<number | null>(null);
  const prevDataRef = useRef<ServiceListDataType[]>([]);
  const prevTotalRef = useRef<number>(0);

  // Fetch filter dropdown options
  const statusesQuery = useGetServicesStatuses();
  const typesQuery = useGetServiceActionProcessTypes();
  const clientsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Client
  );
  const contractsQuery = useGetStructuresByTypeCategoryCodeAndTypeCode(
    StructureTypeCategory.System,
    NodeType.Contract
  );

  // Trigger initial filter option fetches (the underlying queries are
  // enabled: false so they must be refetched manually on mount)
  // Mount-only kick for enabled:false queries. The query objects get a new
  // identity every render, so listing them would refetch per render — the
  // refetch-loop class this file is guarded against.
  useEffect(() => {
    statusesQuery.refetch();
    clientsQuery.refetch();
    contractsQuery.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOptions = useMemo<LabelValue[]>(
    () => buildStatusOptions(statusesQuery.data),
    [statusesQuery.data]
  );

  const statusGroups = useMemo<OptionGroup[]>(
    () => buildStatusGroups(statusesQuery.data),
    [statusesQuery.data]
  );

  const openStatusNames = useMemo<string[]>(
    () => getOpenStatusNames(statusesQuery.data),
    [statusesQuery.data]
  );

  const openStatusFilterModel = useMemo<{ items: GridFilterItem[] }>(
    () => getOpenStatusFilterModel(statusesQuery.data),
    [statusesQuery.data]
  );

  // Finish the deferred open-statuses migration for a saved report that
  // landed before the statuses query resolved. If the user has since
  // populated `serviceStatusName` themselves (manual filter, second
  // report load), abandon the migration so we don't trample their pick.
  useEffect(() => {
    if (!pendingOpenStatusMigration) return;
    if (openStatusFilterModel.items.length === 0) return;
    setFilterModel((prev) => {
      if (hasServiceStatusPredicate(prev.items)) return prev;
      return { ...prev, items: [...prev.items, ...openStatusFilterModel.items] };
    });
    setPendingOpenStatusMigration(false);
  }, [pendingOpenStatusMigration, openStatusFilterModel]);

  // Apply the "all open statuses ticked" default once statuses resolve,
  // unless the URL has already pinned a specific scope, deep-link, or
  // saved report — those provide their own filter source. We don't fire
  // for `savedReportCode` either; useReports resolves that path and calls
  // applyReportConfig, which sets `filtersInitialisedRef` itself.
  useEffect(() => {
    if (filtersInitialisedRef.current) return;
    if (locationId) return;
    if (importSessionId) return;
    if (structureNodeIdParam) return;
    if (clientCode && serviceTypeName && serviceStatusGroup) return;
    if (defaultReport) return;
    if (searchParams.get('savedReportCode')) return;
    if (openStatusNames.length === 0) return;

    setFilterModel(openStatusFilterModel);
    filtersInitialisedRef.current = true;
  }, [
    openStatusFilterModel,
    openStatusNames.length,
    locationId,
    importSessionId,
    structureNodeIdParam,
    clientCode,
    serviceTypeName,
    serviceStatusGroup,
    defaultReport,
    searchParams,
  ]);

  const jobTypeOptions = useMemo<LabelValue[]>(
    () => buildJobTypeOptions(typesQuery.data),
    [typesQuery.data]
  );

  // Services' third quick-filter dropdown lists clients (not contracts) —
  // selecting one filters the BFF by `clientCode`. The hook field name stays
  // `contractOptions` so the FilterBar's shared third-slot prop name lines up
  // with the existing FilterState.contracts shape.
  const contractOptions = useMemo<LabelValue[]>(
    () => buildClientOptions(clientsQuery.data),
    [clientsQuery.data]
  );

  // The genuine contract dropdown (the slot above is clients). Needs the
  // clients list too, to render each contract's owning client code.
  const contractNameOptions = useMemo<LabelValue[]>(
    () => buildContractOptions(contractsQuery.data, clientsQuery.data),
    [contractsQuery.data, clientsQuery.data]
  );

  const {
    statusCodeToName,
    statusNameToCodes,
    clientIdToName,
    clientNameToId,
    openStatusFilterState,
  } = useServiceLookups(statusesQuery.data, clientsQuery.data);

  const queryOptions: ServiceQueryOptions = useMemo(
    () => ({
      filterModel,
      page,
      pageSize,
      sortModel: [sortModel],
      locationId,
      multiSearchQuery: multiSearchQuery || undefined,
      defaultReport,
    }),
    [filterModel, page, pageSize, sortModel, locationId, multiSearchQuery, defaultReport]
  );

  // Mirror filter/sort/pagination state back to the URL so a back-button
  // remount (the list page unmounts when the user clicks into detail) can
  // re-hydrate from URL search params. Replace mode keeps every keystroke
  // out of browser history — only navigation between pages records.
  useEffect(() => {
    const direction = sortModel.sort === 'asc' || sortModel.sort === 'desc' ? sortModel.sort : null;
    const sortToken: SortToken | null =
      sortModel.field && direction ? { field: sortModel.field, direction } : null;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        writeServiceListUrl(
          next,
          { filterModel, multiSearchQuery, sort: sortToken, page, pageSize },
          DEFAULT_SORT
        );
        return next;
      },
      { replace: true }
    );
  }, [filterModel, multiSearchQuery, sortModel, page, pageSize, setSearchParams]);

  const query = useGetServicePaginatedData(queryOptions, colSelectionData);

  // All manual refetch paths share one loop guard — see useRefetchLoopGuard.
  // The fetch timer starts inside the guarded callback so a call the guard
  // suppresses cannot record a stale start time.
  const guardedRefetch = useRefetchLoopGuard(
    useCallback(() => {
      fetchStartRef.current = performance.now();
      query.refetch();
    }, [query]),
    'Jobs list (/servicesList)'
  );

  const { schedule: scheduleColumnRefetch, cancel: cancelColumnRefetch } =
    useColumnRefetchScheduler(guardedRefetch);

  // Any pending column-triggered refetch is cancelled — the queryOptions
  // refetch will carry the latest colSelectionData, so the debounced one
  // would duplicate.
  useEffect(() => {
    cancelColumnRefetch();
    guardedRefetch();
  }, [queryOptions, cancelColumnRefetch, guardedRefetch]);

  // Track fetch duration when query settles
  useEffect(() => {
    if (!query.isFetching && fetchStartRef.current !== null) {
      setFetchDurationMs(performance.now() - fetchStartRef.current);
      fetchStartRef.current = null;
    }
  }, [query.isFetching]);

  const applyFilters = useCallback((filters: FilterState) => {
    setMultiSearchQuery(filters.multiSearch || '');
    setFilterModel(buildFilterModel(filters));
    setPage(0);
    filtersInitialisedRef.current = true;
    // Manual filter input supersedes any deferred saved-report migration.
    setPendingOpenStatusMigration(false);
  }, []);

  const updateColumnSelection = useCallback(
    (data: ColumnSelectionItem[]) => {
      const prevVisible = getVisibleFields(colSelectionData);
      const nextVisible = getVisibleFields(data);
      // Only newly-added columns need a refetch — hiding columns doesn't
      // make the existing rows stale.
      const hasAddedField = Array.from(nextVisible).some((f) => !prevVisible.has(f));

      setColSelectionData(data);
      setColumns((prev) => prev.map((c) => ({ ...c, visible: nextVisible.has(c.key) })));

      if (hasAddedField) scheduleColumnRefetch();
    },
    [colSelectionData, scheduleColumnRefetch]
  );

  const reorderColumns = useCallback((oldIndex: number, newIndex: number) => {
    setColumns((prev) => reorderByVisibleIndex(prev, oldIndex, newIndex));
  }, []);

  const applyReportConfig = useCallback(
    (
      reportFilterModel: { items: GridFilterItem[] },
      colSelection?: ColumnSelectionItem[],
      columnOrder?: Array<{ field: string }>
    ) => {
      const original = reportFilterModel.items ?? [];

      // Strip the legacy `not Complete` + `not Cancelled` pair before
      // sanitising contradictions, so the chips bar — which derives its
      // state from the same helper — and the BFF query stay in lockstep.
      const sanitized = cleanSavedReportFilterModel({ items: original }).items;

      // Migration: if the saved report originally constrained
      // `serviceStatusName` (even just the legacy hidden defaults) but
      // the cleanup left no status predicate, fall back to the new "all
      // open statuses ticked" default so the report still surfaces the
      // same set of jobs as it did pre-rebuild. Skipped when statuses
      // haven't resolved yet — the filter remains empty until the open
      // status names land, then the default-load effect tops it up.
      const hadStatusPredicate = hasServiceStatusPredicate(original);
      const hasFinalStatusPredicate = hasServiceStatusPredicate(sanitized);
      const needsOpenStatusBaseline = hadStatusPredicate && !hasFinalStatusPredicate;
      const canApplyBaselineNow = needsOpenStatusBaseline && openStatusNames.length > 0;
      const finalItems = canApplyBaselineNow
        ? [...sanitized, ...openStatusFilterModel.items]
        : sanitized;

      setFilterModel({ items: finalItems });
      setMultiSearchQuery('');
      setPage(0);
      filtersInitialisedRef.current = true;
      // Defer the open-statuses migration until statuses resolve when
      // the saved report loaded ahead of the statuses query.
      setPendingOpenStatusMigration(needsOpenStatusBaseline && !canApplyBaselineNow);
      if (colSelection) {
        updateColumnSelection(colSelection);
      }
      // Reports don't store column widths — drop any user-dragged widths so the
      // report renders in its own (unfrozen) layout, then apply its saved order.
      setColumns((prev) =>
        dropColumnWidths(columnOrder ? applyColumnOrder(prev, columnOrder) : prev)
      );
    },
    [openStatusFilterModel, openStatusNames.length, updateColumnSelection]
  );

  // Keep previous data visible during refetch
  useEffect(() => {
    if (query.data?.data) {
      prevDataRef.current = query.data.data;
      prevTotalRef.current = query.data.total ?? 0;
    }
  }, [query.data]);

  const currentData = query.data?.data ?? prevDataRef.current;
  const currentTotal = query.data?.total ?? prevTotalRef.current;

  return {
    data: currentData,
    isLoading: query.isLoading && prevDataRef.current.length === 0,
    isFetching: query.isFetching,
    page,
    pageSize,
    total: currentTotal,
    fetchDurationMs,
    sortModel,
    columns,
    columnSelectionData: colSelectionData,
    filterModel,
    statusOptions,
    statusGroups,
    jobTypeOptions,
    contractOptions,
    contractNameOptions,
    setPage,
    setPageSize,
    setSortModel,
    applyFilters,
    applyReportConfig,
    updateColumnSelection,
    reorderColumns,
    resetColumnsToDefault,
    restoreDefaultColumns,
    setColumnWidth,
    refetch: guardedRefetch,
    multiSearchQuery,
    openStatusFilterModel,
    openStatusNames,
    openStatusFilterState,
    statusCodeToName,
    statusNameToCodes,
    clientIdToName,
    clientNameToId,
  };
};
